import React from 'react';
import { useGetUsersQuery } from '../../redux/api/users';
import type { ProgramUser } from '../../interfaces/CamProgramming.interface';

export const CAM_PROGRAMMERS_GROUP = 'CAM Programmers';

type PinnableUser = Pick<ProgramUser, 'id' | 'email' | 'full_name'> | null | undefined;

const normalizeUserId = (id: string | number | null | undefined) =>
	id == null || id === '' ? null : String(id);

const toUserOption = (user: Pick<ProgramUser, 'id' | 'email' | 'full_name'>) => {
	const value = normalizeUserId(user.id);
	if (!value) {
		return null;
	}
	return {
		label: user.full_name || user.email,
		value,
	};
};

/** Active users in the CAM Programmers group, plus any already-assigned users. */
export function useCamProgrammerUserOptions(options?: {
	skip?: boolean;
	pinnedUsers?: PinnableUser[];
}) {
	const { data: usersData, isLoading } = useGetUsersQuery(
		{ groups__name: CAM_PROGRAMMERS_GROUP },
		{ skip: options?.skip }
	);

	const userOptions = React.useMemo(() => {
		const byId = new Map<string, { label: string; value: string }>();

		for (const user of usersData?.results ?? []) {
			const option = toUserOption(user);
			if (option) {
				byId.set(option.value, option);
			}
		}

		for (const pinned of options?.pinnedUsers ?? []) {
			if (!pinned) {
				continue;
			}
			const option = toUserOption(pinned);
			if (option && !byId.has(option.value)) {
				byId.set(option.value, option);
			}
		}

		return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
	}, [options?.pinnedUsers, usersData?.results]);

	return { userOptions, isLoading };
}

export { normalizeUserId as normalizeCamProgrammerUserId };
